import { useCallback, useMemo } from "react";
import { Flex, Text } from "@radix-ui/themes";

import { Step } from "@/v2/features/sampling/Step";
import { EscalationRuleFormData } from "../EscalationRuleForm";
import { FilterMenu } from "@/v2/components/filter-menu/FilterMenu";
import { useGetTopics } from "@/api/useGetTopics";
import { useGetQuackChatFieldOptions } from "@/v2/entities/interaction-additional-context/InteractionAdditionalContext.hooks";
import { LiveAgentRoutingStrategy } from "@/v2/entities/escalation-rule/EscalationRule.model";
import { EscalationStrategy } from "@/v2/entities/release/Release.model";
import { CompactRadioGroup } from "../components/CompactRadioGroup";
import {
  buildEscalationConditionMenu,
  buildFiltersFromValue,
  ConditionFiltersValue,
  fixCompositeKeys,
  parseFiltersToFormValue,
} from "./escalationConditionFilters";

interface ConditionStepProps {
  value: EscalationRuleFormData;
  onChange: (updates: Partial<EscalationRuleFormData>) => void;
  topicsInUse?: string[];
}

export function ConditionStep({ value, onChange }: ConditionStepProps) {
  const { data: topicsData } = useGetTopics();
  const { data: quackChatFields = [] } = useGetQuackChatFieldOptions();

  const filters = useMemo<ConditionFiltersValue>(
    () =>
      buildFiltersFromValue({
        topics: value.topics,
        customFieldFilters: value.customFieldFilters,
        customFields: quackChatFields,
      }),
    [value.topics, value.customFieldFilters, quackChatFields]
  );

  const handleFilterChange = useCallback(
    (newFilters: ConditionFiltersValue) => {
      const fixed = fixCompositeKeys(newFilters);
      const parsed = parseFiltersToFormValue(fixed);
      onChange({
        ...value,
        topics: parsed.topics,
        customFieldFilters: parsed.customFieldFilters,
      });
    },
    [onChange, value]
  );

  const menu = useMemo(
    () =>
      buildEscalationConditionMenu({
        topics: topicsData ?? [],
        customFields: quackChatFields,
      }),
    [topicsData, quackChatFields]
  );

  const handleLiveAgentRoutingChange = useCallback(
    (val: string) => {
      onChange({
        liveAgentRoutingStrategy: val as LiveAgentRoutingStrategy,
      });
    },
    [onChange]
  );

  const isLiveAgent =
    value.escalationStrategy === EscalationStrategy.LIVE_AGENT;

  return (
    <Step
      index={2}
      label="Conditions"
      description="Define when this rule should trigger"
    >
      <Flex direction={"column"} gap={"4"} width={"100%"}>
        {value.name === "Default Rule" && (
          <Text size="2" color="gray" style={{ marginBottom: "8px" }}>
            This is default escalation path. If no other rule applies,
            interactions will follow this method
          </Text>
        )}

        {value.name !== "Default Rule" && menu.length > 0 && (
          <FilterMenu
            label="Add Rule"
            value={filters}
            onChange={handleFilterChange}
            size="2"
            menu={menu}
            hideCount
          />
        )}

        {isLiveAgent && (
          <Flex direction="column" gap="3" mt="2" style={{ width: "400px" }}>
            <Text size="2" weight="medium" color="gray">
              Live Agent Routing Strategy
            </Text>

            <CompactRadioGroup
              value={
                value.liveAgentRoutingStrategy ??
                LiveAgentRoutingStrategy.ONLINE_AGENTS
              }
              onChange={handleLiveAgentRoutingChange}
              options={[
                {
                  label: "Business Hours",
                  value: LiveAgentRoutingStrategy.BUSINESS_HOURS,
                  description: "Route based on set business hours",
                },
                {
                  label: "Online Agents",
                  value: LiveAgentRoutingStrategy.ONLINE_AGENTS,
                  description: "Route only to currently online agents",
                },
              ]}
            />
          </Flex>
        )}
      </Flex>
    </Step>
  );
}
